/** Count something that happened, for Admin > Usage: an event name and a short detail, nothing about who.
 *  It is sent and forgotten -- a failure must never get in a visitor's way -- and, when the Umami server on this
 *  host is set up, passed to it too. Both honour the browser's Do Not Track. */
import { BASE } from '../api'

type UmamiPayload = Record<string, unknown>
declare global {
  interface Window {
    umami?: { track: (a?: string | ((props: UmamiPayload) => UmamiPayload), data?: Record<string, string>) => void }
  }
}

// Do Not Track, and a browser driven by a program (headless Chrome says so): neither is counted anywhere
const dnt = () => navigator.doNotTrack === '1' || navigator.webdriver === true
// the tools share one address, so the page is sent to Umami by name rather than read from the address bar
let page = ''
let address = ''
const sendPage = () => {
  if (page) window.umami?.track((props) => ({ ...props, url: `${BASE}${address || (page === 'home' ? '' : page)}`, title: page }))
}

/** `at`: for a page view, the page's own address under the site's home, when it is not simply its name.
 *  `data`: what the event is about (a model, a vendor, a country code), sent to Umami with it so Admin > Traffic
 *  can say what people compare; never anything typed by the visitor. The server's own count keeps `detail`. */
export function track(event: string, detail = '', at = '', data?: Record<string, string | number>): void {
  try {
    if (dnt()) return
    const body = JSON.stringify({ event, detail })
    const url = `${BASE}api/event`
    if (!(navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type: 'application/json' })))) {
      void fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => undefined)
    }
    if (event === 'page_view') { page = detail; address = at; sendPage() }
    else window.umami?.track(event, detail || data ? { ...(detail ? { detail } : {}), ...(data ?? {}) } : undefined)
  } catch { /* counting is never worth an error */ }
}

/** Load the Umami tracker from this host once its website id is known; the page being looked at is sent when it arrives. */
export function loadUmami(websiteId: string | null | undefined): void {
  if (!websiteId || dnt() || document.querySelector('script[data-umami]')) return
  const s = document.createElement('script')
  s.defer = true
  s.src = `${BASE}umami/script.js`
  s.setAttribute('data-website-id', websiteId)
  s.setAttribute('data-host-url', `${window.location.origin}${BASE}umami`)
  s.setAttribute('data-auto-track', 'false')
  s.setAttribute('data-do-not-track', 'true')
  s.setAttribute('data-umami', '1')
  s.onload = sendPage
  document.head.appendChild(s)
}

/** An event for Umami alone, with its data: for what the server counts by itself (a sign-up, an export it saved),
 *  so the server's tally is not told twice. */
export function note(event: string, detail = '', data?: Record<string, string | number>): void {
  try {
    if (dnt()) return
    window.umami?.track(event, { ...(detail ? { detail } : {}), ...(data ?? {}) })
  } catch { /* counting is never worth an error */ }
}

/** What a question is about, for the `ask` event: a class, never the words. */
export function askTopic(text: string): string {
  const q = text.toLowerCase()
  if (/\b(ptu|provisioned|reserved|dedicated|commit)/.test(q)) return 'reserved'
  if (/\b(gpu|h100|h200|b200|a100|self-?host|server|vram|instance|cluster)/.test(q)) return 'hardware'
  if (/\b(in|inside|within|held to) (the )?(uae|emirates|dubai|saudi|germany|europe|eu|uk|india|us|united)|\bregion|\bcountry|residency|sovereign/.test(q)) return 'place'
  if (/\b(vs|versus|compare|cheapest|cheaper|best)\b/.test(q)) return 'compare'
  if (/\b(cost|price|pay|spend|budget|token|month)/.test(q)) return 'price'
  return 'other'
}
